import type { QuickPanelParams, QuickPanelRegistryEntry } from './quick-panel-registry.js';

import type { QuickPanelStackEntry } from '@/app/surfaces/quick-panel-provider.js';
import { resolveRegisterPartyTrail } from '@/features/customer/party-entry-context.js';

function isStringTrail(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.length >= 2 && value.every((crumb) => typeof crumb === 'string')
  );
}

export function resolvePanelTitle(
  entry: { params: QuickPanelParams },
  registryEntry: Pick<QuickPanelRegistryEntry, 'title'>,
): string {
  if (typeof entry.params['title'] === 'string') {
    return entry.params['title'];
  }

  return registryEntry.title;
}

function resolveRegisterPartyContextualTrail(entry: QuickPanelStackEntry): string[] | null {
  if (entry.key !== 'register-party' && entry.key !== 'customers-register') {
    return null;
  }

  const rawContext = entry.params['entryContext'];
  if (rawContext !== 'CUSTOMER' && rawContext !== 'SUPPLIER' && rawContext !== 'NEUTRAL') {
    return null;
  }

  return [...resolveRegisterPartyTrail(rawContext)];
}

function resolveContextualTrailCrumbs(
  topEntry: QuickPanelStackEntry,
  topRegistry: QuickPanelRegistryEntry,
): string[] {
  if (isStringTrail(topEntry.params['trail'])) {
    return topEntry.params['trail'];
  }

  const registerPartyTrail = resolveRegisterPartyContextualTrail(topEntry);
  if (registerPartyTrail) {
    return registerPartyTrail;
  }

  const trailParent =
    typeof topEntry.params['trailParent'] === 'string'
      ? topEntry.params['trailParent']
      : topRegistry.trailParent;

  if (typeof trailParent === 'string' && trailParent.length > 0) {
    return [trailParent, resolvePanelTitle(topEntry, topRegistry)];
  }

  return [resolvePanelTitle(topEntry, topRegistry)];
}

export function resolveQuickPanelTrailCrumbsForEntry(
  stack: QuickPanelStackEntry[],
  entryIndex: number,
  registryLookup: (key: string) => QuickPanelRegistryEntry | undefined,
): string[] {
  if (stack.length === 0 || entryIndex < 0 || entryIndex >= stack.length) {
    return [];
  }

  if (stack.length > 1) {
    return stack.slice(0, entryIndex + 1).map((trailEntry) => {
      const trailRegistryEntry = registryLookup(trailEntry.key);
      return trailRegistryEntry
        ? resolvePanelTitle(trailEntry, trailRegistryEntry)
        : trailEntry.key;
    });
  }

  const entry = stack[entryIndex];
  if (!entry) {
    return [];
  }

  const registry = registryLookup(entry.key);
  if (!registry) {
    return [];
  }

  return resolveContextualTrailCrumbs(entry, registry);
}

export function resolveQuickPanelTrailCrumbs(
  stack: QuickPanelStackEntry[],
  registryLookup: (key: string) => QuickPanelRegistryEntry | undefined,
): string[] {
  if (stack.length === 0) {
    return [];
  }

  return resolveQuickPanelTrailCrumbsForEntry(stack, stack.length - 1, registryLookup);
}
